import * as THREE from 'three';
import { resolveEntityItemOpacity } from '../../_pure/itemOpacity.js';
import { resolveCylinderWallGeometrySpecs, resolveExtrudedProfileGeometrySpecs, resolveItemOutlineGeometrySpec, } from './geometrySpec.js';
import { toDeclaredPlacementOffset } from './labelResolution.js';
import { resolveDoubleSided, resolveItemMaterialSpec, resolveOutlineMaterialSpec, usesBorderHighlightCue } from './materialSpec.js';
import { resolveProfilePlacement } from './profilePlacement.js';
import { resolveSectorSliceCompression } from './sectorSliceSurface.js';
import { resolveEntityItemActiveHighlightColor, resolveEntityItemColor, resolveEntityItemLinkedHighlightColor, } from './spaceRendererHelpers.js';
import { SPOKE_PANEL_LABEL_ROTATION, normalizeSpokePanelLabelOffset } from './spokePanelLabelTransform.js';
export function resolveItemRenderPlan(args) {
    const { shape, layout, placement, geometry, itemParametricState, precedence, defaultColor, dimNonActiveOpacity } = args;
    const color = resolveEntityItemColor(itemParametricState, defaultColor);
    const opacity = precedence.resolvedOpacity;
    const cameraProximityFade = itemParametricState?.cameraProximityFade;
    if (shape?.shapeKind === 'cylinder-wall') {
        return {
            position: placement.position,
            quaternion: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(...shape.axis).normalize()),
            scale: [1, Math.max(shape.height, 1e-6), 1],
            surfaces: resolveCylinderWallGeometrySpecs(shape).map((spec, index) => ({
                key: String(index), geometry: spec,
                material: { materialKind: 'basic', color,
                    opacity: opacity * (spec.radius === shape.outerRadius ? shape.outerOpacity : shape.innerOpacity),
                    doubleSided: true, transparent: true, depthWrite: false },
                renderOrder: -1,
            })),
            outlines: [], labels: false, interaction: 'pass-through',
            ...(shape.cutaway ? { cameraFacingCutaway: true } : {}),
            ...(cameraProximityFade ? { cameraProximityFade,
                proximityVolume: { radius: shape.outerRadius, height: shape.height } } : {}),
        };
    }
    if (shape?.shapeKind === 'extruded-profile') {
        const profile = resolveProfilePlacement(shape.from, shape.to, shape.lateral);
        if (!profile)
            return null;
        const specs = resolveExtrudedProfileGeometrySpecs(shape);
        const border = shape.border;
        return {
            position: placement.position, localPosition: profile.position, quaternion: profile.quaternion,
            scale: [1, profile.length, 1],
            surfaces: specs.map(spec => ({
                key: spec.surface, geometry: spec,
                material: { materialKind: 'standard', color: spec.surface === 'floor' ? shape.floorColor ?? '#24272a' : color,
                    opacity, doubleSided: false },
            })),
            outlines: border && (border.opacity ?? 1) > 0
                ? specs.filter(spec => spec.surface !== 'floor').map(spec => ({
                    key: `${spec.surface}:outline`, geometry: { ...spec, outline: true },
                    material: { materialKind: 'line-basic', color: border.color ?? color,
                        opacity: opacity * (border.opacity ?? 1), width: border.width ?? 1 },
                })) : [],
            labels: itemParametricState?.label?.aboveItem !== undefined,
            ...(itemParametricState?.label?.aboveItem === undefined ? {} : { labelFrame: 'placement' }),
            interaction: 'none',
        };
    }
    const outline = resolveItemOutlineGeometrySpec(shape);
    return {
        position: placement.position, rotation: placement.rotation,
        surfaces: [{ key: 'face', geometry, material: resolveItemMaterialSpec({
                    precedence, itemParametricState, shape, baseOpacity: resolveEntityItemOpacity(itemParametricState),
                    defaultColor, doubleSided: resolveDoubleSided({ layout, shape }),
                }) }],
        outlines: outline ? [{ key: 'outline', geometry: outline, material: resolveOutlineMaterialSpec({
                    precedence, itemParametricState, shape, defaultColor, dimNonActiveOpacity,
                }) }] : [],
        labels: true, interaction: itemParametricState?.interaction?.enabled === false ? 'none' : 'item',
        ...(layout.layoutKind === 'sector-ring' && (layout.outerThickness ?? 0) > 0
            ? { normalScale: resolveSectorSliceCompression(precedence.isActive, layout.activeThicknessScale, itemParametricState?.thicknessScale) } : {}),
        ...(cameraProximityFade ? { cameraProximityFade } : {}),
        ...resolveSilhouetteContour({ shape, geometry, itemParametricState, precedence }),
    };
}
const SILHOUETTE_GAP_FRACTION = 0.2;
const SILHOUETTE_WIDTH_PIXELS = 2.5;
/**
 * A sphere's contour shows its own border, or its active/linked highlight when highlights use the border cue.
 * The highlight wins over the border; explicit dimming and hiding suppress the highlight.
 */
export function resolveSilhouetteContour(args) {
    const { shape, geometry, itemParametricState, precedence } = args;
    if (shape?.shapeKind !== 'sphere' || geometry.geometryKind !== 'sphere')
        return {};
    const border = shape.border;
    const cue = usesBorderHighlightCue(itemParametricState, shape);
    if (!border && !cue)
        return {};
    const highlight = !cue || precedence.isExplicitlyDimmed || precedence.isExplicitlyHidden ? undefined
        : precedence.isActive ? resolveEntityItemActiveHighlightColor(itemParametricState)
            : precedence.isLinkedActive ? resolveEntityItemLinkedHighlightColor(itemParametricState) : undefined;
    const color = highlight ?? border?.color ?? resolveEntityItemColor(itemParametricState, 'white');
    const shown = highlight !== undefined || (border !== undefined && !precedence.isExplicitlyHidden);
    const offset = border?.offset ?? geometry.radius * SILHOUETTE_GAP_FRACTION;
    return {
        silhouetteContour: {
            radius: geometry.radius + Math.max(0, offset),
            color,
            opacity: shown ? clamp01(precedence.resolvedOpacity * (highlight ? 1 : border?.opacity ?? 1)) : 0,
            width: border?.width ?? SILHOUETTE_WIDTH_PIXELS,
        },
    };
}
function clamp01(value) {
    return Math.min(1, Math.max(0, value));
}
export function resolveItemLabelTransform(shape) {
    if (shape?.shapeKind !== 'panel')
        return undefined;
    return ({ channel, declaration }) => channel !== 'onItem' ? undefined : ({
        position: normalizeSpokePanelLabelOffset(toDeclaredPlacementOffset(declaration.placement)),
        localRotation: new THREE.Quaternion().setFromEuler(new THREE.Euler(...SPOKE_PANEL_LABEL_ROTATION, 'XYZ')),
        localRotationMode: 'replace',
    });
}
